import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, Zap, Bell, Eye, Check, Loader2 } from 'lucide-react';
import { createSubscriptionCheckout, getSubscriptionStatus } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const Premium = () => {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your account.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await getSubscriptionStatus(sid);
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Welcome to Premium!');
        await refreshUser();
        setCheckingPayment(false);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setCheckingPayment(false);
    }
  };

  const handleSubscribe = async (plan) => {
    if (!user) {
      toast.error('Please login to subscribe');
      return;
    }

    if (user.is_premium) {
      toast.info('You are already a Premium member!');
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await createSubscriptionCheckout(plan, originUrl);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setLoading(false);
    }
  };

  const features = [
    { icon: Eye, title: 'Early Access', desc: 'See new drops 24 hours before everyone else' },
    { icon: Bell, title: 'Instant Alerts', desc: 'Real-time notifications for price drops' },
    { icon: Zap, title: 'Exclusive Deals', desc: 'Premium-only discounts and offers' },
    { icon: Crown, title: 'Priority Support', desc: 'Direct line to our sneaker experts' },
  ];

  const plans = [
    {
      id: 'monthly',
      name: 'Monthly',
      price: 9.99,
      period: '/month',
      features: ['All premium features', 'Cancel anytime', 'Instant activation'],
    },
    {
      id: 'yearly',
      name: 'Yearly',
      price: 79.99,
      period: '/year',
      features: ['All premium features', '33% savings', 'Cancel anytime', 'Priority support'],
      popular: true,
    },
  ];

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="text-[#D2FF00] animate-spin mx-auto mb-4" />
          <h2 className="font-display text-2xl mb-2">PROCESSING PAYMENT</h2>
          <p className="text-white/60">Please wait while we confirm your payment...</p>
        </div>
      </div>
    );
  }

  if (user?.is_premium) {
    return (
      <div className="min-h-screen bg-[#050505] py-24 px-6 lg:px-12" data-testid="premium-page">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="w-24 h-24 bg-[#D2FF00] rounded-full flex items-center justify-center mx-auto mb-8">
              <Crown size={48} className="text-black" />
            </div>
            <h1 className="font-display text-5xl uppercase mb-4">YOU'RE PREMIUM</h1>
            <p className="text-white/60 text-lg mb-8">
              Enjoy all the exclusive benefits of your Premium membership!
            </p>
            <div className="grid grid-cols-2 gap-4 mb-8">
              {features.map((feature) => (
                <div key={feature.title} className="bg-[#0A0A0A] p-6 text-left">
                  <feature.icon className="text-[#D2FF00] mb-3" size={24} />
                  <h3 className="font-medium mb-1">{feature.title}</h3>
                  <p className="text-white/40 text-sm">{feature.desc}</p>
                </div>
              ))}
            </div>
            <Link to="/shop">
              <Button className="btn-primary">START SHOPPING</Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="premium-page">
      {/* Hero */}
      <section className="py-24 px-6 lg:px-12 relative overflow-hidden">
        <div className="absolute inset-0 glow" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block bg-[#D2FF00] text-black text-xs font-bold uppercase px-3 py-1 mb-6">
              LEVEL UP
            </span>
            <h1 className="font-display text-6xl sm:text-7xl uppercase mb-6">
              GO <span className="text-[#D2FF00]">PREMIUM</span>
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Get early access to limited drops, instant price alerts, and exclusive deals. 
              Never miss another release.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-6 lg:px-12 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-6"
              >
                <div className="w-16 h-16 bg-[#D2FF00]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="text-[#D2FF00]" size={28} />
                </div>
                <h3 className="font-display text-lg uppercase mb-2">{feature.title}</h3>
                <p className="text-white/60 text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-4xl uppercase mb-4">CHOOSE YOUR PLAN</h2>
            <p className="text-white/60">Cancel anytime. No questions asked.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {plans.map((plan) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`relative bg-[#0A0A0A] p-8 ${
                  plan.popular ? 'border-2 border-[#D2FF00]' : 'border border-white/10'
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D2FF00] text-black text-xs font-bold uppercase px-3 py-1">
                    BEST VALUE
                  </span>
                )}
                <h3 className="font-display text-2xl uppercase mb-2">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="font-display text-5xl">${plan.price}</span>
                  <span className="text-white/60">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-white/80">
                      <Check size={16} className="text-[#D2FF00]" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={loading}
                  className={`w-full ${plan.popular ? 'btn-primary' : 'btn-secondary'}`}
                  data-testid={`subscribe-${plan.id}`}
                >
                  {loading ? 'LOADING...' : 'SUBSCRIBE'}
                </Button>
              </motion.div>
            ))}
          </div>

          {!user && (
            <p className="text-center text-white/40 mt-8">
              <Link to="/login" className="text-[#D2FF00] hover:underline">Sign in</Link> or create an account to subscribe
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default Premium;
